/**
 * Plan Context
 *
 * Manages user's subscription plan, features, Gmail accounts, and admin status globally
 * Caches data to prevent flashing/loading on tab switches
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getUserPlan, getUserFeatures, updateUserPlan } from '../services/planService';
import { supabase } from '../services';
import { gmailService } from '../services';

const PlanContext = createContext();

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

export const PlanProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [userFeatures, setUserFeatures] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [syncedAccounts, setSyncedAccounts] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(true);

  // Load plan and features once when user is available
  useEffect(() => {
    const loadPlanData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Check if plan was just updated (e.g., after payment)
        const planUpdated = sessionStorage.getItem('plan_updated');
        if (planUpdated === 'true') {
          sessionStorage.removeItem('plan_updated');
        }

        // Load plan, features, and subscription info in parallel
        const [plan, features, subInfo] = await Promise.all([
          getUserPlan(),
          getUserFeatures(),
          getSubscriptionInfo()
        ]);

        setCurrentPlan(plan);
        setUserFeatures(features);
        setSubscriptionInfo(subInfo);
      } catch (error) {
        console.error('Error loading plan data:', error);
      } finally {
        setLoading(false);
      }
    };

    // Helper function to get subscription info
    const getSubscriptionInfo = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('current_period_end, subscription_status, cancel_at_period_end, billing_day_of_month, billing_interval')
          .eq('user_id', user.id)
          .single();

        if (error) throw error;
        return data;
      } catch (error) {
        console.error('Error fetching subscription info:', error);
        return null;
      }
    };

    loadPlanData();

    // Also listen for plan update events (e.g., after payment)
    const handleStorageChange = (e) => {
      if (e.key === 'plan_updated' && e.newValue === 'true') {
        loadPlanData();
      }
    };

    // Listen for custom plan update event
    const handlePlanUpdate = () => {
      loadPlanData();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('plan-updated', handlePlanUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('plan-updated', handlePlanUpdate);
    };
  }, [user?.id]);

  // Load Gmail accounts once when user is available
  useEffect(() => {
    const loadGmailAccounts = async () => {
      if (!user?.id) {
        setAccountsLoading(false);
        return;
      }

      try {
        const accounts = await gmailService.getGmailAccounts();
        setSyncedAccounts(accounts || []);
      } catch (error) {
        console.error('Error loading Gmail accounts:', error);
        setSyncedAccounts([]);
      } finally {
        setAccountsLoading(false);
      }
    };

    loadGmailAccounts();
  }, [user?.id]);

  // Check admin status once when user is available
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.id) {
        setAdminLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('admins')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        setIsAdmin(!!data && !error);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setAdminLoading(false);
      }
    };

    checkAdminStatus();
  }, [user?.id]);

  // Function to update plan (used when user upgrades/dowgrades)
  const changePlan = async (newPlan) => {
    const success = await updateUserPlan(newPlan);
    if (success) {
      // Reload plan data
      await refreshPlan();
    }
    return success;
  };

  // Function to manually refresh plan data
  const refreshPlan = async () => {
    try {
      const [plan, features, subInfo] = await Promise.all([
        getUserPlan(),
        getUserFeatures(),
        (async () => {
          const { data } = await supabase
            .from('user_profiles')
            .select('current_period_end, subscription_status, cancel_at_period_end, billing_day_of_month, billing_interval')
            .eq('user_id', user.id)
            .single();
          return data;
        })()
      ]);
      setCurrentPlan(plan);
      setUserFeatures(features);
      setSubscriptionInfo(subInfo);
    } catch (error) {
      console.error('Error refreshing plan data:', error);
    }
  };

  // Function to manually refresh Gmail accounts
  const refreshGmailAccounts = async () => {
    try {
      setAccountsLoading(true);
      const accounts = await gmailService.getGmailAccounts();
      setSyncedAccounts(accounts || []);
    } catch (error) {
      console.error('Error refreshing Gmail accounts:', error);
    } finally {
      setAccountsLoading(false);
    }
  };

  // Function to add a Gmail account to cache (deduplicates by id)
  const addGmailAccount = (account) => {
    setSyncedAccounts(prev => [...prev.filter(a => a.id !== account.id), account]);
  };

  // Function to remove a Gmail account from cache
  const removeGmailAccount = (accountId) => {
    setSyncedAccounts(prev => prev.filter(acc => acc.id !== accountId));
  };

  // Function to update syncing status for an account
  const updateAccountSyncStatus = (accountId, isSyncing) => {
    setSyncedAccounts(prev =>
      prev.map(acc =>
        acc.id === accountId ? { ...acc, isSyncing } : acc
      )
    );
  };

  const value = {
    // Plan data
    currentPlan,
    userFeatures,
    subscriptionInfo,
    loading,
    changePlan,
    refreshPlan,

    // Gmail accounts
    syncedAccounts,
    accountsLoading,
    refreshGmailAccounts,
    addGmailAccount,
    removeGmailAccount,
    updateAccountSyncStatus,

    // Admin status
    isAdmin,
    adminLoading
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

export default PlanContext;
